import { Stack, Typography } from '@mui/material';
import { filter, isEmpty } from 'lodash';
import React, { useState } from 'react';

// Components
import LoadingSkeleton from 'src/components/common/LoadingSkeleton';
import CompanyInfoNoData from 'src/features/CompanyDetail/Common/CompanyInfoNoData';
import QuickViewContact from 'src/features/CompanyDetail/Common/QuickViewContact';
import { StyledWhiteBox } from 'src/features/CompanyDetail/config';
import ContactDB from 'src/features/CompanyDetail/ContactsByAI/ContactDB';
import ContactAI from 'src/features/CompanyDetail/ContactsByAI/ContactAI';

// Utilities
import { DefinedUseQueryResult } from '@tanstack/react-query';
import { TypeContributeCompany } from 'src/features/CompanyDetail/Common/config';
import { CompanyContactsByAI } from 'src/model/company';
import { CompanyContactPositionCode } from 'src/utils/constants/enum';
import { getArrayDefault } from 'src/utils/helpers/arrayHelpers';
import { useCompanyContribute } from 'src/zustand/useCompanyContribute';

const ContactsByAITab = ({ contact }: { contact: DefinedUseQueryResult<CompanyContactsByAI, Error> }) => {
  const { data, isFetching } = contact;
  const listDataContactsDB = getArrayDefault<Array<IContactCompanyManager>>(data?.data?.db_managers);
  const listDataContactsAI = getArrayDefault<Array<IContactCompanyManagerAI>>(data?.data?.ai_managers);
  const listOtherContacts = filter(listDataContactsDB, (contact: IContactCompanyManager) =>
    [CompanyContactPositionCode.Unknown, CompanyContactPositionCode.Employee].includes(contact.position_code),
  );
  const listContactsManager = filter(
    listDataContactsDB,
    (contact: IContactCompanyManager) =>
      ![CompanyContactPositionCode.Unknown, CompanyContactPositionCode.Employee].includes(contact.position_code),
  );

  const isEmptyData = isEmpty(listDataContactsDB) && isEmpty(listDataContactsAI);

  const [open, setOpen] = useState(false);
  const [idSelect, setIdSelect] = useState('');
  const { handleOpenContribute } = useCompanyContribute((state) => state);

  const handleClose = () => {
    setOpen(false);
  };

  const handleOpen = (id: string) => {
    setIdSelect(id);
    setOpen(true);
  };

  if (isFetching) {
    return (
      <StyledWhiteBox>
        <LoadingSkeleton count={5} height={50} />
      </StyledWhiteBox>
    );
  }

  if (isEmptyData) {
    return (
      <CompanyInfoNoData
        title="No Contacts Found"
        content={
          <>
            Please consider{' '}
            <Typography
              variant="body1"
              color="secondary.main"
              component="span"
              onClick={() => handleOpenContribute(TypeContributeCompany.ContactInformation)}
              sx={{ cursor: 'pointer' }}
            >
              contributing your own data{' '}
            </Typography>
            .
          </>
        }
      />
    );
  }

  return (
    <React.Fragment>
      {open && <QuickViewContact id={idSelect} open={open} handleClose={handleClose} />}
      <Stack spacing={3}>
        {getArrayDefault(listContactsManager).length > 0 && (
          <StyledWhiteBox>
            <ContactDB handleOpen={handleOpen} listContact={getArrayDefault(listContactsManager)} title="Managers" />
          </StyledWhiteBox>
        )}
        {getArrayDefault(listOtherContacts).length > 0 && (
          <StyledWhiteBox>
            <ContactDB
              handleOpen={handleOpen}
              listContact={getArrayDefault(listOtherContacts)}
              title="Other Contacts"
            />
          </StyledWhiteBox>
        )}
        {getArrayDefault(listDataContactsAI).length > 0 && (
          <StyledWhiteBox>
            <ContactAI listContact={getArrayDefault(listDataContactsAI)} title="Suggested By AI" />
          </StyledWhiteBox>
        )}
      </Stack>
    </React.Fragment>
  );
};

export default ContactsByAITab;
